import * as React from 'react'
import { Languages, RefreshCw, TriangleAlert } from 'lucide-react'
import { NativeSelect } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { useT } from '@/components/visitone/visitone-provider'
import { useLanguages } from '@/hooks/use-languages'
import { useTenantContext } from '@/components/tenant/tenant-context'
import { useLanguageContext } from '@/components/language/language-context'

/*
 * Tenant language selector. The BFF is the source of truth for which languages
 * a tenant offers, so the control renders the four states VIS-252 requires:
 * loading, error (with retry), empty (no selectable language), and the list of
 * languages returned for the current tenant. When languages load, the first
 * available language becomes the selection unless a still-valid selection is
 * already held for this tenant. Only languages returned for the current tenant
 * are ever shown.
 */
export function LanguageSelector() {
  const t = useT()
  const { currentTenantId: tenantId } = useTenantContext()
  const { languages, error, isLoading, retry } = useLanguages(tenantId)
  const { getLanguage, setLanguage } = useLanguageContext()

  const selected = tenantId ? getLanguage(tenantId) : null

  React.useEffect(() => {
    if (!tenantId || !languages || languages.length === 0) return
    const stillValid = selected && languages.some((language) => language.code === selected)
    if (!stillValid) setLanguage(tenantId, languages[0].code)
  }, [tenantId, languages, selected, setLanguage])

  // No active tenant yet, or the list is loading.
  if (!tenantId || isLoading) {
    return <Skeleton className="h-10 w-40" aria-label={t('core.languageSelector.loading')} />
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={() => retry()}
        className="inline-flex h-10 items-center gap-2 rounded-sm border border-danger/40 bg-surface px-3 text-sm text-danger transition-colors hover:border-danger"
      >
        <TriangleAlert aria-hidden="true" className="size-4" />
        <span>{t('core.languageSelector.errorTitle')}</span>
        <RefreshCw aria-hidden="true" className="size-3.5" />
        <span className="sr-only">{t('core.languageSelector.retry')}</span>
      </button>
    )
  }

  // Empty state: the tenant offers no languages, so nothing is selectable.
  if (!languages || languages.length === 0) {
    return (
      <span className="inline-flex h-10 items-center gap-2 rounded-sm border border-line bg-surface px-3 text-sm text-fg-muted">
        <Languages aria-hidden="true" className="size-4" />
        {t('core.languageSelector.empty')}
      </span>
    )
  }

  return (
    <div className="inline-flex items-center gap-2">
      <Languages aria-hidden="true" className="size-4 text-fg-muted" />
      <label>
        <span className="sr-only">{t('core.languageSelector.label')}</span>
        <NativeSelect
          value={selected ?? ''}
          onChange={(event) => {
            if (tenantId) setLanguage(tenantId, event.target.value)
          }}
          className="h-10 w-40"
        >
          {languages.map((language) => (
            <option key={language.code} value={language.code}>
              {language.name}
            </option>
          ))}
        </NativeSelect>
      </label>
    </div>
  )
}
